import type { BotSettings, Locale } from "@ai-admin/shared";
import { Locales } from "@ai-admin/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { queryClient, trpc } from "../../app/trpc.js";
import { Button, Card, FormRow, Input, Notice, PageTitle, SectionTitle, Select, Spinner, Textarea, Toggle } from "../../ui/index.js";
import { useCabinet } from "./context.js";

const lines = (s: string) =>
  s
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);

const LANG_LABELS: Record<Locale, string> = { "sr-Latn": "Srpski (latinica)", "sr-Cyrl": "Српски (ћирилица)", en: "English", ru: "Русский" };

const AUTONOMY = ["auto", "confirm", "answer_only"] as const satisfies readonly BotSettings["autonomy"][];

/** «Кто отвечает»: выключатель бота, самостоятельность, тишина мастера, возврат бота, исключения, раскрытие, языки. */
export function BotSettingsPage() {
  const { t } = useTranslation();
  const cab = useCabinet();
  const q = useQuery(trpc.bot.settings.get.queryOptions({ salonId: cab.salonId }));
  const [form, setForm] = useState<BotSettings | null>(null);
  const [topics, setTopics] = useState("");
  const [ignored, setIgnored] = useState("");
  const save = useMutation(
    trpc.bot.settings.update.mutationOptions({ onSuccess: (data) => queryClient.setQueryData(trpc.bot.settings.get.queryKey({ salonId: cab.salonId }), data) }),
  );

  useEffect(() => {
    if (q.data && !form) {
      setForm(q.data);
      setTopics(q.data.alwaysHandoffTopics.join("\n"));
      setIgnored(q.data.ignoredContacts.join("\n"));
    }
  }, [q.data, form]);

  if (q.isPending || !form) return <Spinner />;
  const set = <K extends keyof BotSettings>(key: K, value: BotSettings[K]) => {
    save.reset();
    setForm({ ...form, [key]: value });
  };
  const num = (key: "masterSilenceMin" | "botResumeAfterHumanMin" | "confirmTimeoutMin" | "maxNoShowsBeforeHandoff") => ({
    value: String(form[key]),
    inputMode: "numeric" as const,
    onChange: (e: { target: { value: string } }) => set(key, Number(e.target.value.replace(/\D/g, "")) || 0),
  });
  const toggleLang = (l: Locale, on: boolean) => {
    const next = on ? [...form.languages, l] : form.languages.filter((x) => x !== l);
    if (next.length > 0)
      set(
        "languages",
        Locales.filter((x) => next.includes(x)),
      );
  };
  const onSave = () => save.mutate({ salonId: cab.salonId, patch: { ...form, alwaysHandoffTopics: lines(topics), ignoredContacts: lines(ignored) } });

  return (
    <div className="lg:max-w-3xl">
      <PageTitle>{t("bot.title")}</PageTitle>
      <Card>
        <Toggle checked={form.enabled} onChange={(v) => set("enabled", v)} label={t("bot.enabled")} hint={t("bot.enabledHint")} />
      </Card>

      <SectionTitle>{t("bot.autonomyTitle")}</SectionTitle>
      <Card className="space-y-3">
        {/* Варианты карточками, а не списком: длинная подпись в списке на телефоне обрезается, здесь переносится. */}
        <fieldset className="min-w-0 space-y-2">
          <legend className="sr-only">{t("bot.autonomyTitle")}</legend>
          {AUTONOMY.map((mode) => (
            // Нативная радиогруппа: стрелки, Tab и экранные чтецы работают сами; кружок рисуем свой.
            <label
              key={mode}
              className={`flex w-full cursor-pointer items-start gap-3 rounded-field border px-4 py-3 transition has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand has-[:focus-visible]:ring-offset-2 ${
                form.autonomy === mode ? "border-brand bg-brand-soft ring-1 ring-brand" : "border-line bg-white hover:border-line-strong"
              }`}
            >
              <input
                type="radio"
                name="autonomy"
                value={mode}
                checked={form.autonomy === mode}
                onChange={() => set("autonomy", mode)}
                className="peer sr-only"
              />
              <span
                aria-hidden="true"
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${form.autonomy === mode ? "border-brand" : "border-line-strong"}`}
              >
                {form.autonomy === mode && <span className="h-2.5 w-2.5 rounded-full bg-brand" />}
              </span>
              <span className="min-w-0">
                <span className="block font-medium">{t(`bot.autonomy.${mode}`)}</span>
                <span className="block text-sm text-ink-3">{t(`bot.autonomyHint.${mode}`)}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {form.autonomy === "confirm" && (
          <div className="space-y-3">
            <FormRow label={t("bot.confirmTimeout")} htmlFor="confirm" hint={t("bot.confirmTimeoutHint")}>
              <Input id="confirm" {...num("confirmTimeoutMin")} />
            </FormRow>
            <FormRow label={t("bot.onConfirmTimeout")} htmlFor="onTimeout">
              <Select id="onTimeout" value={form.onConfirmTimeout} onChange={(e) => set("onConfirmTimeout", e.target.value as BotSettings["onConfirmTimeout"])}>
                <option value="confirm">{t("bot.onConfirmTimeoutOptions.confirm")}</option>
                <option value="reject">{t("bot.onConfirmTimeoutOptions.reject")}</option>
              </Select>
            </FormRow>
          </div>
        )}
      </Card>

      <SectionTitle>{t("bot.whenTitle")}</SectionTitle>
      <Card className="space-y-3">
        <FormRow label={t("bot.masterSilence")} htmlFor="silence" hint={t("bot.masterSilenceHint")}>
          <Input id="silence" {...num("masterSilenceMin")} />
        </FormRow>
        <FormRow label={t("bot.resumeAfter")} htmlFor="resume" hint={t("bot.resumeAfterHint")}>
          <Input id="resume" {...num("botResumeAfterHumanMin")} />
        </FormRow>
        <FormRow label={t("bot.noShows")} htmlFor="noshows">
          <Input id="noshows" {...num("maxNoShowsBeforeHandoff")} />
        </FormRow>
      </Card>

      <SectionTitle>{t("bot.exceptionsTitle")}</SectionTitle>
      <Card className="space-y-3">
        <FormRow label={t("bot.topics")} htmlFor="topics">
          <Textarea id="topics" rows={3} value={topics} onChange={(e) => setTopics(e.target.value)} placeholder={t("bot.topicsPlaceholder")} />
        </FormRow>
        <FormRow label={t("bot.ignored")} htmlFor="ignored" hint={t("bot.ignoredHint")}>
          <Textarea id="ignored" rows={3} value={ignored} onChange={(e) => setIgnored(e.target.value)} placeholder="@marko_ns" />
        </FormRow>
      </Card>

      <SectionTitle>{t("bot.languagesTitle")}</SectionTitle>
      <Card>
        {Locales.map((l) => (
          <Toggle key={l} checked={form.languages.includes(l)} onChange={(v) => toggleLang(l, v)} label={LANG_LABELS[l]} />
        ))}
        <Toggle checked={form.disclosureEnabled} onChange={(v) => set("disclosureEnabled", v)} label={t("bot.disclosure")} hint={t("bot.disclosureHint")} />
      </Card>

      <div className="sticky bottom-[calc(5rem+env(safe-area-inset-bottom))] mt-6 space-y-2 lg:static">
        {save.isError && <Notice kind="error">{t("common.error")}</Notice>}
        {save.isSuccess && <Notice kind="success">{t("settings.saved")}</Notice>}
        <Button className="w-full shadow-lg lg:w-auto" onClick={onSave} disabled={save.isPending}>
          {t("settings.save")}
        </Button>
      </div>
    </div>
  );
}
